import AttachmentTable from './AttachmentsTable';
import { SideSheet, SideSheetHeader } from '../../../layouts';
import {
  convertDateToDayJs,
  formatDayJsDate,
} from '../../../utils/dateTimeUtils';
import dayjs from 'dayjs';

interface ModeOfJourneyLineItemDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  lineItemData: any;
  attachments: any[];
  travelRequestId: number;
  formConfig: any;
  headerChildren?: React.ReactNode;
}

function GridDataPoint({
  config,
}: {
  config: { label: string; value: string };
}) {
  const { label, value } = config;
  return (
    <div className="flex flex-col gap-y-1">
      <div className="font-semibold text-text-200 text-xs">{label}</div>
      <div className="font-normal text-text-400 text-xs">{value}</div>
    </div>
  );
}

const ModeOfJourneyLineItemDetails = ({
  isOpen,
  onClose,
  lineItemData,
  attachments,
  travelRequestId,
  formConfig,
  headerChildren,
}: ModeOfJourneyLineItemDetailsProps) => {
  if (!isOpen) return null;

  const fields = formConfig?.modeOfJourneyForm?.fields;

  return (
    <SideSheet
      isOpen={isOpen}
              addOnClass="w-full max-w-none md:!w-[50vw]"
      closeOnClickOutside={true}
      onClose={onClose}
      contentChildren={
        <div className="flex flex-col gap-4 p-3">
          {lineItemData && (
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {fields?.modeOfJourney?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.modeOfJourney?.label ||
                        fields?.modeOfJourney?.defaultLabel ||
                        'Mode of Journey',
                      value:
                        lineItemData.modeOfJourney?.label ||
                        lineItemData.travelModeOfJourney?.name ||
                        '-',
                    }}
                  />
                )}

                {fields?.fromDate?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.fromDate?.label ||
                        fields?.fromDate?.defaultLabel ||
                        'From Date',
                      value: lineItemData.fromDate
                        ? formatDayJsDate(
                            convertDateToDayJs(lineItemData.fromDate),
                          )
                        : '-',
                    }}
                  />
                )}

                {fields?.fromTime?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.fromTime?.label ||
                        fields?.fromTime?.defaultLabel ||
                        'From Time',
                      value: lineItemData.fromTime
                        ? dayjs(lineItemData.fromTime, 'HH:mm').format(
                            'hh:mm A',
                          )
                        : '-',
                    }}
                  />
                )}

                {fields?.fromCountry?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.fromCountry?.label ||
                        fields?.fromCountry?.defaultLabel ||
                        'From Country',
                      value:
                        lineItemData.fromCountry?.label ||
                        lineItemData.travelFromCountry?.LongDescription ||
                        '-',
                    }}
                  />
                )}

                {fields?.fromCity?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.fromCity?.label ||
                        fields?.fromCity?.defaultLabel ||
                        'From City',
                      value:
                        lineItemData.fromCity?.label ||
                        lineItemData.travelFromCity?.ShortDescription ||
                        '-',
                    }}
                  />
                )}

                {fields?.toDate?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.toDate?.label ||
                        fields?.toDate?.defaultLabel ||
                        'To Date',
                      value: lineItemData.toDate
                        ? formatDayJsDate(
                            convertDateToDayJs(lineItemData.toDate),
                          )
                        : '-',
                    }}
                  />
                )}

                {fields?.toTime?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.toTime?.label ||
                        fields?.toTime?.defaultLabel ||
                        'To Time',
                      value: lineItemData.toTime
                        ? dayjs(lineItemData.toTime, 'HH:mm').format('hh:mm A')
                        : '-',
                    }}
                  />
                )}

                {fields?.toCountry?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.toCountry?.label ||
                        fields?.toCountry?.defaultLabel ||
                        'To Country',
                      value:
                        lineItemData.toCountry?.label ||
                        lineItemData.travelToCountry?.LongDescription ||
                        '-',
                    }}
                  />
                )}

                {fields?.toCity?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.toCity?.label ||
                        fields?.toCity?.defaultLabel ||
                        'To City',
                      value:
                        lineItemData.toCity?.label ||
                        lineItemData.travelToCity?.ShortDescription ||
                        '-',
                    }}
                  />
                )}

                {fields?.remarks?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.remarks?.label ||
                        fields?.remarks?.defaultLabel ||
                        'Remarks',
                      value: lineItemData.remarks || '-',
                    }}
                  />
                )}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <h2 className="text-md">Attachments</h2>
            <AttachmentTable
              attachments={attachments || []}
              travelRequestId={travelRequestId}
            />
          </div>
        </div>
      }
      headerChildren={
        <div className="border-b border-gray-100">
          {headerChildren || (
            <SideSheetHeader
              title="Mode of Journey Details"
              onCloseClick={onClose}
            />
          )}
        </div>
      }
      footerChildren={<></>}
    />
  );
};

export default ModeOfJourneyLineItemDetails;
